import {
  ArrowUpOnSquareIcon,
  ChevronUpIcon,
  ClockIcon,
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { UseMutationResult } from "react-query";
import { Modal } from "../shared-components/Modal";
import {
  classNames,
  formattedISOUTC,
  ISOToMilliseconds,
  LONG_TEXT_WRAP_CLASSES,
} from "../utils";
import { usePreventLeave } from "../utils/usePreventLeave";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  ConfirmationDialogIcon,
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../shared-components/ConfirmationDialog";
import { ButtonColorTypes } from "../shared-components/Button";
import {
  useGetItemDocumentUploadUrl,
  useUploadFileToS3,
  useSanitizeItemDocument,
  useGetItemDocuments,
  useDeleteItemDocument,
  useDeleteAllItemDocuments,
} from "../api/360FilesAPI";
import ExtractFromFiles from "./ExtractFromFiles";
import { inspectPdf, pdfLocalCheckMessage } from "./looksLikePdf";
import { useCreateReportTimer } from "../api/ReportsApi";
import { ACTOR_TIMER_STATUS_ENUM } from "../api/Reports";

const PDF_ONLY_ERROR = "Please upload only PDF files";
const PDF_DOC_ONLY_ERROR = "Please upload only PDF, DOC, or DOCX files";

type RejectedUploadReason = "type" | "encrypted" | "not-pdf";

interface RejectedUpload {
  file: File;
  reason: RejectedUploadReason;
}

function rejectedUploadMessage(
  reason: RejectedUploadReason,
  typeErrorMessage: string
): string {
  if (reason === "type") return typeErrorMessage;
  return pdfLocalCheckMessage(reason === "encrypted" ? "encrypted" : "not-pdf");
}

function isPdfFile(file: File): boolean {
  if (file.type === "application/pdf") return true;
  return file.name.toLowerCase().endsWith(".pdf");
}

function isPdfOrDocFile(file: File): boolean {
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  if (allowedTypes.includes(file.type)) return true;
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".pdf") || name.endsWith(".doc") || name.endsWith(".docx")
  );
}

interface FormField {
  id: string;
  label: string;
  type: string;
  placeholder: string;
  required?: boolean;
}

interface AttachedDocumentsProps {
  title?: string;
  author?: string;
  showAddItem?: boolean;
  fields?: FormField[];
  report_uuid?: string;
  workflow_run_uuid?: string;
  items?: any[];
  isLoading?: boolean;
  error?: string;
  createItemMutation?: UseMutationResult<any, Error, any>;
  updateItemMutation?: UseMutationResult<any, Error, any>;
  removeItemMutation?: UseMutationResult<any, Error, any>;
  singleItem?: boolean;
  modalHeaderAction?: React.ReactNode;
  person_uuid?: string;
  item_type?: string;
  onExtractedFromFiles?: (items: any[]) => void;
  bgColorClass?: string;
  sortBy?: string;
  DocRenderer?: React.FC<{ doc: any }>;
  headerTitleAction?: React.ReactNode;
  headerSecondaryAction?: React.ReactNode;
  confirmRemove?: boolean;
  invalidateQuery?: () => void;
  fileUploadType?: "pdf" | "pdf-doc" | "any";
}

const fileUploadRules: Record<
  NonNullable<AttachedDocumentsProps["fileUploadType"]>,
  {
    accept?: string;
    errorMessage?: string;
    validate?: (file: File) => boolean;
  }
> = {
  pdf: {
    accept: "application/pdf,.pdf",
    errorMessage: PDF_ONLY_ERROR,
    validate: isPdfFile,
  },
  "pdf-doc": {
    accept:
      "application/pdf,.pdf,application/msword,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx",
    errorMessage: PDF_DOC_ONLY_ERROR,
    validate: isPdfOrDocFile,
  },
  any: {},
};

const AttachedDocuments: React.FC<AttachedDocumentsProps> = ({
  title = "<title>",
  author = "",
  showAddItem = true,
  fields = [],
  report_uuid = "",
  items = [],
  isLoading = false,
  error = "",
  createItemMutation = null,
  updateItemMutation = null,
  removeItemMutation = null,
  singleItem = false,
  modalHeaderAction,
  person_uuid,
  item_type,
  onExtractedFromFiles,
  bgColorClass,
  sortBy = "",
  DocRenderer,
  headerTitleAction,
  headerSecondaryAction,
  confirmRemove = false,
  workflow_run_uuid,
  invalidateQuery,
  fileUploadType = "pdf",
}) => {
  const fileUploadRule = fileUploadRules[fileUploadType] ?? fileUploadRules.pdf;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItemId, setCurrentItemId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const { canEdit360Reports } = usePermissionValidator();
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<RejectedUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isCheckingFiles, setIsCheckingFiles] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const getItemUploadUrl = useGetItemDocumentUploadUrl();
  const uploadToS3 = useUploadFileToS3();
  const sanitizeItemDocument = useSanitizeItemDocument();
  const inputRef = useRef<HTMLInputElement>(null);
  const fileCheckGenerationRef = useRef(0);
  const inFlightFileChecksRef = useRef(0);
  // Bumped when this modal session ends. An in-flight create/update must not
  // close or retarget a modal the user opened afterward.
  const submitGenerationRef = useRef(0);
  const deleteItemDocumentMutation = useDeleteItemDocument();
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const { data: uploadedFiles = [], refetch: refetchUploadedFiles } =
    useGetItemDocuments(currentItemId ?? "", true);
  const deleteAllItemDocumentsMutation = useDeleteAllItemDocuments();
  const createReportTimerMutation = useCreateReportTimer();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    modal: removeConfirmationModal,
    show: showRemoveConfirmationModal,
    hide: hideRemoveConfirmationModal,
  } = useConfirmationDialog();

  // Extract from files modal state
  const [showExtractModal, setShowExtractModal] = useState(false);

  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timerStartTime, setTimerStartTime] = useState<number | null>(null);

  const handleTimerLeave = useCallback(() => {
    const finishedAt = formattedISOUTC(new Date().toISOString());
    const finishedAtMilliseconds = ISOToMilliseconds(finishedAt);
    const timerRequestBase = {
      name: title,
      value: finishedAtMilliseconds,
      workflow_run_uuid: workflow_run_uuid ?? "",
      finished_at: finishedAt,
      status: ACTOR_TIMER_STATUS_ENUM.CLOSED,
    };
    setIsTimerRunning(false);
    createReportTimerMutation.mutate(timerRequestBase);
    setTimerStartTime(null);
  }, [title, workflow_run_uuid, createReportTimerMutation]);

  const { LeaveWarningModal } = usePreventLeave({
    when: isTimerRunning,
    title: "Timer Running",
    message:
      "There is a timer running. If you leave this page, the timer will stop.",
    onLeave: handleTimerLeave,
  });

  if (!singleItem && items.length > 1 && sortBy !== "") {
    items.sort((a, b) => {
      if (sortBy in a && sortBy in b) {
        const da = Date.parse(a[sortBy]);
        if (!da) {
          return 1;
        }
        const db = Date.parse(b[sortBy]);
        if (!db) {
          return -1;
        }
        // newest on top (reverse)
        return db - da;
      }
      return 0;
    });
  }

  const beginFileCheck = () => {
    inFlightFileChecksRef.current += 1;
    setIsCheckingFiles(true);
    return fileCheckGenerationRef.current;
  };

  const endFileCheck = (generation: number) => {
    if (generation !== fileCheckGenerationRef.current) return;
    inFlightFileChecksRef.current = Math.max(
      0,
      inFlightFileChecksRef.current - 1
    );
    if (inFlightFileChecksRef.current === 0) {
      setIsCheckingFiles(false);
    }
  };

  const cancelPendingFileChecks = () => {
    fileCheckGenerationRef.current += 1;
    inFlightFileChecksRef.current = 0;
    setIsCheckingFiles(false);
  };

  const resetPendingUploads = () => {
    submitGenerationRef.current += 1;
    cancelPendingFileChecks();
    setPendingFiles([]);
    setRejectedFiles([]);
    setUploadError(null);
    setUploadSuccess(null);
    setUploading(false);
    setIsSubmitting(false);
  };

  const handleOpenModal = () => {
    resetPendingUploads();
    setIsModalOpen(true);
    setFormData({});
    setEditMode(false);
    setCurrentItemId(null);
    setErrorMessage(null);
  };

  const handleEditModal = (item: any) => {
    resetPendingUploads();
    setIsModalOpen(true);
    setFormData(item);
    setEditMode(true);
    setCurrentItemId(item.external_id);
    setErrorMessage(null);
  };

  const handleCloseModal = () => {
    resetPendingUploads();
    setIsModalOpen(false);
    setEditMode(false);
    setCurrentItemId(null);
    setErrorMessage(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  async function addPendingFiles(files: File[]) {
    const generation = beginFileCheck();
    const accepted: File[] = [];
    const rejected: RejectedUpload[] = [];

    try {
      for (const f of files) {
        if (generation !== fileCheckGenerationRef.current) return;
        if (fileUploadRule.validate && !fileUploadRule.validate(f)) {
          rejected.push({ file: f, reason: "type" });
          continue;
        }
        if (isPdfFile(f)) {
          const check = await inspectPdf(f);
          if (generation !== fileCheckGenerationRef.current) return;
          if (!check.ok) {
            rejected.push({
              file: f,
              reason: check.reason === "encrypted" ? "encrypted" : "not-pdf",
            });
            continue;
          }
        }
        accepted.push(f);
      }
    } finally {
      // React 17 does not batch setState after await; keep file results and
      // the checking flag in the same commit so Create cannot run against [].
      unstable_batchedUpdates(() => {
        if (generation === fileCheckGenerationRef.current) {
          if (rejected.length) {
            setRejectedFiles((prev) => [...prev, ...rejected]);
          }
          if (accepted.length) {
            setPendingFiles((prev) => [...prev, ...accepted]);
          }
        }
        endFileCheck(generation);
      });
    }
  }

  function handleFileInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (isSubmitting || uploading || !e.target.files?.length) return;
    const files = Array.from(e.target.files);
    e.target.value = "";
    void addPendingFiles(files);
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
    if (isSubmitting || uploading || !event.dataTransfer.files.length) return;
    const files = Array.from(event.dataTransfer.files);
    void addPendingFiles(files);
  }

  function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();
  }

  function handleClick() {
    if (isSubmitting || uploading) return;
    inputRef.current?.click();
  }

  async function removeRejectedUpload(uuid: string, filename: string) {
    try {
      await deleteItemDocumentMutation.mutateAsync({
        itemUuid: uuid,
        filename,
      });
    } catch (deleteErr) {
      console.error("Failed to remove rejected upload:", deleteErr);
    }
  }

  function uploadFailureMessage(file: File, detail?: string): string {
    return detail
      ? `Failed to upload: ${file.name}. ${detail}`
      : `Failed to upload: ${file.name}`;
  }

  async function handleFileUpload(file: File, uuid: string): Promise<void> {
    try {
      if (isPdfFile(file)) {
        const check = await inspectPdf(file);
        if (!check.ok) {
          throw new Error(
            uploadFailureMessage(file, pdfLocalCheckMessage(check.reason))
          );
        }
      }
      const { upload_url, object_key } = await getItemUploadUrl.mutateAsync({
        item_uuid: uuid,
        filename: file.name,
      });
      await uploadToS3.mutateAsync({ uploadUrl: upload_url, file });
      if (object_key && isPdfFile(file)) {
        try {
          await sanitizeItemDocument.mutateAsync({
            item_uuid: uuid,
            object_key,
          });
        } catch (sanitizeErr) {
          await removeRejectedUpload(uuid, file.name);
          console.error("Sanitize error:", sanitizeErr);
          throw new Error(
            uploadFailureMessage(
              file,
              "This PDF is damaged and could not be repaired, so it was not saved."
            )
          );
        }
      }
    } catch (err) {
      console.error("Upload error:", err);
      if (err instanceof Error && err.message.startsWith("Failed to upload:")) {
        throw err;
      }
      throw new Error(uploadFailureMessage(file));
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inFlightFileChecksRef.current > 0) return;
    const filesToUpload = pendingFiles;
    const submitGeneration = submitGenerationRef.current;
    const isCurrentSubmit = () =>
      submitGeneration === submitGenerationRef.current;
    setIsSubmitting(true);
    setErrorMessage(null);
    // Lock the picker before create/update returns. Otherwise the drop zone
    // stays active and setPendingFiles(failed) can overwrite later edits.
    if (filesToUpload.length) setUploading(true);

    const dataToSend: Record<string, string | null> = { ...formData };
    fields.forEach((field) => {
      if (field.type === "file") {
        delete dataToSend[field.id];
      } else if (field.type === "date" && dataToSend[field.id] === "") {
        dataToSend[field.id] = null;
      }
    });

    const uploadFiles = async (itemId: string): Promise<boolean> => {
      if (!isCurrentSubmit()) return false;
      if (!filesToUpload.length) return true;
      if (!itemId) {
        setUploading(false);
        return true;
      }
      setUploadError(null);
      setUploadSuccess(null);
      const succeeded: File[] = [];
      const successLines: string[] = [];
      const errorLines: string[] = [];
      try {
        for (const file of filesToUpload) {
          if (!isCurrentSubmit()) return false;
          try {
            await handleFileUpload(file, itemId);
            succeeded.push(file);
            successLines.push(`Uploaded: ${file.name}`);
          } catch (err) {
            errorLines.push(
              err instanceof Error ? err.message : uploadFailureMessage(file)
            );
          }
        }
      } finally {
        // Do not return from finally; that would discard the try result.
        // A newer modal session owns this state once the generation changes.
        if (isCurrentSubmit()) {
          // React 17 does not batch setState after await. Remove only files this
          // batch uploaded so a picker change during create/update is not lost.
          const uploaded = new Set(succeeded);
          unstable_batchedUpdates(() => {
            setUploading(false);
            setPendingFiles((prev) =>
              prev.filter((file) => !uploaded.has(file))
            );
            setUploadSuccess(
              successLines.length ? successLines.join("\n") : null
            );
            setUploadError(errorLines.length ? errorLines.join("\n") : null);
          });
        }
      }
      return errorLines.length === 0;
    };

    const unlockFilePicker = () => {
      setIsSubmitting(false);
      setUploading(false);
    };

    const ignoreStaleSubmit = () => {
      if (isCurrentSubmit()) return false;
      invalidateQuery?.();
      return true;
    };

    if (editMode) {
      updateItemMutation?.mutate(
        { id: currentItemId, data: dataToSend },
        {
          onSuccess: async (data) => {
            if (ignoreStaleSubmit()) return;
            const itemId = currentItemId || data?.data?.external_id;
            const uploaded = await uploadFiles(itemId);
            if (ignoreStaleSubmit()) return;
            refetchUploadedFiles();
            invalidateQuery?.();
            setIsSubmitting(false);
            if (uploaded) handleCloseModal();
          },
          onError: () => {
            if (!isCurrentSubmit()) return;
            unlockFilePicker();
            setErrorMessage("Could not update item, please try again.");
          },
        }
      );
      return;
    }

    createItemMutation?.mutate(dataToSend, {
      onSuccess: async (data) => {
        if (ignoreStaleSubmit()) return;
        const itemId = data?.data?.external_id;
        const uploaded = await uploadFiles(itemId);
        if (ignoreStaleSubmit()) return;
        setCurrentItemId(itemId);
        refetchUploadedFiles();
        invalidateQuery?.();
        setIsSubmitting(false);
        if (uploaded) {
          handleCloseModal();
        } else {
          setEditMode(true);
        }
      },
      onError: () => {
        if (!isCurrentSubmit()) return;
        unlockFilePicker();
        setErrorMessage("Could not create item, please try again.");
      },
    });
  };

  const handleRemoveItem = (itemId: string) => {
    if (!removeItemMutation) return;
    deleteAllItemDocumentsMutation.mutate(itemId);
    removeItemMutation.mutate(itemId);
  };

  const requestRemoveItem = (itemId: string) => {
    if (!confirmRemove) {
      handleRemoveItem(itemId);
      return;
    }

    showRemoveConfirmationModal(
      "Delete item?",
      "Are you sure you want to delete this item? This action cannot be undone.",
      (dialogAction: ConfirmDialogActions) => {
        if (dialogAction !== "continue") return;
        hideRemoveConfirmationModal();
        handleRemoveItem(itemId);
      },
      undefined,
      true,
      "Cancel",
      ButtonColorTypes.cancel,
      "Yes, Delete",
      ButtonColorTypes.secondary,
      ConfirmationDialogIcon.DELETE
    );
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isTimerRunning && timerStartTime !== null) {
      interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - timerStartTime) / 1000);
        setElapsedSeconds(elapsed);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, timerStartTime]);

  // Format elapsed seconds to HH:MM:SS
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(secs).padStart(2, "0")}`;
  };

  const handleTimerToggle = () => {
    const finishedAt = formattedISOUTC(new Date().toISOString());
    const finishedAtMilliseconds = ISOToMilliseconds(finishedAt);
    const timerRequestBase = {
      name: title,
      value: finishedAtMilliseconds,
      workflow_run_uuid: workflow_run_uuid ?? "",
      finished_at: finishedAt,
      status: isTimerRunning
        ? ACTOR_TIMER_STATUS_ENUM.CLOSED
        : ACTOR_TIMER_STATUS_ENUM.OPEN,
    };
    if (isTimerRunning) {
      setIsTimerRunning(false);
      createReportTimerMutation.mutate(timerRequestBase);
      setTimerStartTime(null);
    } else {
      const now = Date.now();
      const startTime = now - elapsedSeconds * 1000;
      setTimerStartTime(startTime);
      setIsTimerRunning(true);

      createReportTimerMutation.mutate(timerRequestBase);
    }
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-lg border border-gray-300 shadow-sm overflow-hidden">
        <div className="bg-gray-50 p-4 border-b border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0 mr-4">
              <div
                className={classNames(
                  "w-6 h-6 rounded-full flex items-center justify-center text-white text-xs",
                  bgColorClass ?? ""
                )}
              >
                {items?.length}
              </div>
            </div>
            <div className="flex-grow min-w-0">
              <h3
                className={classNames(
                  "text-gray-700 font-medium text-base flex items-center",
                  LONG_TEXT_WRAP_CLASSES
                )}
              >
                {title}
                {headerTitleAction}
              </h3>
            </div>
            <ChevronUpIcon
              className={classNames(
                "w-5 flex-none text-gray-500 cursor-pointer",
                isExpanded ? "rotate-180" : ""
              )}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          </div>
          {isExpanded && canEdit360Reports && (
            <div className="my-2 flex flex-row items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2">
                <div
                  className={classNames(
                    "rounded-lg border shadow-sm bg-white p-1 w-20 cursor-pointer",
                    isTimerRunning ? "border-red-500" : "border-green-500"
                  )}
                  onClick={handleTimerToggle}
                >
                  <div className="flex items-center gap-1">
                    <div className="flex items-center justify-center">
                      <ClockIcon
                        className={classNames(
                          "w-4 h-4",
                          isTimerRunning ? "text-red-500" : "text-green-500"
                        )}
                      />
                    </div>
                    <div>
                      <span
                        className={classNames(
                          "font-medium",
                          isTimerRunning ? "text-red-600" : "text-green-600"
                        )}
                      >
                        {isTimerRunning ? "Stop" : "Start"}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <span className="text-gray-500 font-medium">
                    {formatTime(elapsedSeconds)}
                  </span>
                </div>
              </div>
              {headerSecondaryAction}
            </div>
          )}
        </div>
        {isExpanded && (
          <>
            {isLoading && (
              <div className="flex-grow">
                <LoadingSpinner />
              </div>
            )}

            {singleItem && !isLoading && (
              <>
                <div
                  key={items?.[0]?.external_id}
                  className="flex flex-row items-center gap-4 w-full flex-none order-2 self-stretch p-4"
                >
                  <div className="flex-grow">
                    {items?.length > 0 ? (
                      <>
                        <span className="text-gray-500 text-xs">
                          New Summary
                        </span>
                      </>
                    ) : (
                      <p className="text-gray-500 text-xs">New Summary</p>
                    )}
                  </div>
                  {canEdit360Reports && (
                    <div className="flex items-center space-x-2 flex-none ">
                      <PencilSquareIcon
                        className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700"
                        onClick={() => handleEditModal(items?.[0])}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
            {!singleItem && !isLoading && (
              <div className="py-4 flex flex-col gap-4">
                {items?.length > 0 ? (
                  items.map((item) => (
                    <div
                      key={item.external_id}
                      className="flex flex-row items-start gap-4 w-full min-w-0 flex-none order-2 self-stretch px-3 last:mb-0 border-b border-gray-200 last:border-b-0 pb-3 last:pb-0"
                    >
                      <div
                        className={classNames(
                          "flex-grow",
                          LONG_TEXT_WRAP_CLASSES
                        )}
                      >
                        {DocRenderer ? (
                          <DocRenderer doc={item} />
                        ) : (
                          <>
                            <h3 className="text-gray-700 font-medium text-sm">
                              {item.title}
                            </h3>
                            <p className="text-gray-500 text-xs">
                              {item.author}
                            </p>
                          </>
                        )}
                      </div>
                      {canEdit360Reports && (
                        <div className="flex items-center space-x-2 flex-none">
                          <PencilSquareIcon
                            className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700"
                            onClick={() => handleEditModal(item)}
                          />
                          <TrashIcon
                            className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-700"
                            onClick={() => {
                              const itemId = item.external_id ?? "";
                              if (!itemId) return;
                              requestRemoveItem(itemId);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-gray-500 text-xs py-1 px-3">
                    No items added
                  </div>
                )}
              </div>
            )}

            {showAddItem && canEdit360Reports && (
              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <div className="flex gap-2 justify-between items-center w-full text-gray-500 text-sm">
                  <button
                    type="button"
                    className="flex items-center text-gray-500 hover:text-gray-700 gap-1 transition-colors duration-200 py-1 border-none"
                    onClick={handleOpenModal}
                  >
                    <PlusCircleIcon className="w-4 h-4" strokeWidth="2" />
                    <span className="font-medium">Add Item</span>
                  </button>
                  {title === "News and Web" && (
                    <button
                      type="button"
                      className="flex items-center text-gray-500 hover:text-gray-700 gap-1 transition-colors duration-200 py-1 border-none"
                      onClick={() => setShowExtractModal(true)}
                    >
                      <ArrowUpOnSquareIcon
                        className="w-4 h-4"
                        strokeWidth="2"
                      />
                      <span className="font-medium">Extract from files</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {isModalOpen && (
        <Modal
          open={isModalOpen}
          onClose={handleCloseModal}
          title={editMode ? `Edit ${title}` : `Add ${title}`}
          width={classNames(
            title !== "Summary"
              ? "w-full max-w-md"
              : "w-full max-w-2xl h-[900px] flex flex-col"
          )}
          height="h-full"
        >
          {!editMode && modalHeaderAction}
          {(!currentItemId || editMode) && title !== "Summary" && (
            <form onSubmit={handleSubmit} className="pt-6">
              {fields.map((field) => (
                <div key={field.id} className="mb-4">
                  <label
                    htmlFor={field.id}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {field.label}
                    {field.required && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </label>
                  {field.type === "file" ? (
                    <>
                      {uploading ? (
                        <div className="flex flex-col items-center justify-center py-8">
                          <LoadingSpinner className="w-8 h-8 mb-2" />
                          <span className="text-sm text-muted-foreground">
                            Uploading files...
                          </span>
                        </div>
                      ) : (
                        <div
                          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors ${
                            isSubmitting
                              ? "opacity-50 pointer-events-none"
                              : "hover:border-primary"
                          }`}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onClick={handleClick}
                          aria-disabled={isSubmitting}
                          tabIndex={0}
                          role="button"
                        >
                          <input
                            ref={inputRef}
                            type="file"
                            className="hidden"
                            onChange={handleFileInputChange}
                            aria-label="Upload file"
                            disabled={isSubmitting}
                            multiple
                            accept={fileUploadRule.accept}
                          />
                          <span className="text-sm text-muted-foreground">
                            Drag & drop or{" "}
                            <span className="underline">browse</span> to upload
                          </span>
                        </div>
                      )}

                      {isCheckingFiles && !uploading && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                          <LoadingSpinner className="w-4 h-4" />
                          <span>Checking files...</span>
                        </div>
                      )}

                      {pendingFiles.length > 0 && !uploading && (
                        <ul className="mt-2 w-full text-xs text-left space-y-1">
                          {pendingFiles.map((file, idx) => (
                            <li
                              key={file.name + idx}
                              className="flex items-center justify-between"
                            >
                              <span className="truncate">{file.name}</span>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-700 text-xs"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSubmitting || uploading) return;
                                  setPendingFiles((prev) =>
                                    prev.filter((_, i) => i !== idx)
                                  );
                                }}
                                disabled={isSubmitting || uploading}
                                aria-label={`Remove ${file.name}`}
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}

                      {rejectedFiles.length > 0 && !uploading && (
                        <div
                          className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3  text-red-700"
                          role="alert"
                        >
                          <p className="text-xs">
                            The following files could not be added:
                          </p>
                          <ul className="mt-2 space-y-2">
                            {rejectedFiles.map((rejected, idx) => (
                              <li
                                key={rejected.file.name + idx}
                                className="flex items-start gap-2 text-xs"
                              >
                                <ExclamationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                                <div className="min-w-0 flex-grow">
                                  <div className="truncate font-medium">
                                    {rejected.file.name}
                                  </div>
                                  <div className="mt-1 text-xs">
                                    {rejectedUploadMessage(
                                      rejected.reason,
                                      fileUploadRule.errorMessage ??
                                        "Invalid file type"
                                    )}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {uploadError && (
                        <div className="mt-2 whitespace-pre-line rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                          {uploadError}
                        </div>
                      )}
                      {uploadSuccess && (
                        <div className="mt-2 whitespace-pre-line text-xs text-green-600">
                          {uploadSuccess}
                        </div>
                      )}
                    </>
                  ) : field.type === "textarea" ? (
                    <textarea
                      id={field.id}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder={field.placeholder}
                      rows={3}
                      value={formData[field.id] || ""}
                      onChange={handleInputChange}
                      required={field.required}
                    />
                  ) : (
                    <input
                      type={field.type}
                      id={field.id}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder={field.placeholder}
                      value={formData[field.id] || ""}
                      onChange={handleInputChange}
                      required={field.required}
                    />
                  )}
                </div>
              ))}

              {editMode && uploadedFiles.length > 0 && (
                <div className="mt-2 text-xs text-green-700">
                  <div>Uploaded files:</div>
                  <ul className="space-y-1 mt-2">
                    {uploadedFiles.map((doc: any) => (
                      <li
                        key={String(doc.filename)}
                        className="flex items-center gap-2 min-w-0"
                      >
                        <a
                          href={String(doc.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classNames(
                            "underline flex-1",
                            LONG_TEXT_WRAP_CLASSES
                          )}
                        >
                          {String(doc.filename)}
                        </a>
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700 text-xs border border-red-200 rounded px-2 py-0.5 shrink-0"
                          onClick={() => setFileToDelete(String(doc.filename))}
                          disabled={deleteItemDocumentMutation.isLoading}
                          aria-label={`Delete ${String(doc.filename)}`}
                        >
                          Delete
                        </button>
                      </li>
                    ))}
                  </ul>
                  {/* Confirm delete modal */}
                  {fileToDelete && (
                    <Modal
                      open={!!fileToDelete}
                      onClose={() => setFileToDelete(null)}
                      title="Delete file?"
                      width="max-w-sm"
                      height=""
                    >
                      <div className="py-4 text-sm text-gray-700 min-w-0">
                        Are you sure you want to delete{" "}
                        <span
                          className={classNames(
                            "font-semibold",
                            LONG_TEXT_WRAP_CLASSES
                          )}
                        >
                          {fileToDelete}
                        </span>
                        ?
                      </div>
                      <div className="flex justify-end gap-2 mt-4">
                        <button
                          type="button"
                          className="px-3 py-1 rounded border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 text-xs"
                          onClick={() => setFileToDelete(null)}
                          disabled={deleteItemDocumentMutation.isLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1 rounded border border-red-500 text-white bg-red-600 hover:bg-red-700 text-xs"
                          onClick={() => {
                            if (!currentItemId || !fileToDelete) return;
                            deleteItemDocumentMutation.mutate(
                              {
                                itemUuid: currentItemId,
                                filename: fileToDelete,
                              },
                              {
                                onSuccess: () => {
                                  setFileToDelete(null);
                                  refetchUploadedFiles();
                                },
                              }
                            );
                          }}
                          disabled={deleteItemDocumentMutation.isLoading}
                        >
                          {deleteItemDocumentMutation.isLoading
                            ? "Deleting..."
                            : "Delete"}
                        </button>
                      </div>
                    </Modal>
                  )}
                </div>
              )}

              {errorMessage && (
                <div className="mt-2 text-xs text-red-600">{errorMessage}</div>
              )}

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting || isCheckingFiles}
                >
                  {isSubmitting
                    ? editMode
                      ? "Saving..."
                      : "Creating..."
                    : isCheckingFiles
                    ? "Checking files..."
                    : editMode
                    ? "Save Item"
                    : "Create Item"}
                </button>
              </div>
            </form>
          )}
        </Modal>
      )}

      {/* Extract from files modal */}
      {title === "News and Web" && (
        <ExtractFromFiles
          open={showExtractModal}
          onClose={() => setShowExtractModal(false)}
          person_uuid={person_uuid ?? ""}
          item_type={item_type ?? ""}
          onExtracted={onExtractedFromFiles ?? (() => {})}
          title="Extract news from files"
        />
      )}

      {LeaveWarningModal}

      {removeConfirmationModal}
    </div>
  );
};

export default AttachedDocuments;
